import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { RestfulEndpoint } from '../restful-endpoint.service';
import {
  BarChart,
  ChartData,
  PieChart,
  ProjectsReportStatisticsModel,
  ObservationsChartDataModel,
  UserChart,
} from '../../models/analytics.model';
import { AccessLevel } from '../../core/user/user.model';
import { AuthService } from '../../core/auth/auth.service';
import { TopUserData, ObservationType } from '../../models/observations.model';

// Re-export for backward compatibility
export { ObservationType } from '../../models/observations.model';

export type ProjectsArchiveFilter = 'all' | 'archived' | 'not_archived';

export const FULL_STATISTIC_TYPES = [
  'projects_per_month',
  'reports_per_month',
  'observations_on_360_per_month',
  'observations_on_floor_plan_per_month',
  'observations_on_document_per_month',
  'images_per_month',
  'files_per_month',
] as const;

export type FullStatisticType = (typeof FULL_STATISTIC_TYPES)[number];

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    private endPoint: RestfulEndpoint,
    private _auth: AuthService,
  ) {}

  private _observationChartData: {
    categoriesChart?: BarChart[];
    userChart?: UserChart[];
    pieChart?: PieChart;
    assigneeChart?: TopUserData;
  } = null;

  // chart data container
  get observationChartData(): {
    categoriesChart?: BarChart[];
    userChart?: UserChart[];
    pieChart?: PieChart;
    assigneeChart?: TopUserData;
  } {
    return this._observationChartData;
  }

  getAnalyticChartPermission() {
    return from(this._auth.getPermission(AccessLevel.SYSTEM_VIEW_ANALYTICS_CHARTS));
  }

  getAnalyticReportPermission() {
    return from(this._auth.getPermission(AccessLevel.SYSTEM_VIEW_PROJECT_REPORT));
  }

  getSummaryReportPermission(): boolean {
    return JSON.parse(localStorage.getItem('user-data')).permissions.includes(
      'SYSTEM_ACCESS_ADMIN_SETTINGS',
    );
  }

  getExportPdfPermission() {
    return from(this._auth.getPermission(AccessLevel.SYSTEM_EXPORT_TO_PDF));
  }

  private _cumulativeObservationChartData: {
    categoriesChart?: BarChart[];
    userChart?: UserChart[];
    pieChart?: PieChart;
  } = null;

  get cumulativeObservationChartData(): {
    categoriesChart?: BarChart[];
    userChart?: UserChart[];
    pieChart?: PieChart;
  } {
    return this._cumulativeObservationChartData;
  }

  /* ******************
   *  *** Fetch Methods Start  ***
   *  ****************** */

  //#region--Fetches

  getObservationTypes(): Observable<{ observation_types: ObservationType[] }> {
    return this.endPoint.post('get_observation_types', {});
  }

  getFullStats(
    year: any,
    projectId?: string | string[],
    observationTypes?: string[],
    statistic?: FullStatisticType,
  ): Observable<any> {
    let params: any = {
      project_ids: Array.isArray(projectId) ? projectId : [projectId],
    };
    !params.project_ids[0] ? delete params['project_ids'] : null;
    if (year !== null && year !== undefined) {
      params.year = year;
    }

    // Add observation_types if provided and not empty
    if (observationTypes && observationTypes.length > 0) {
      params.observation_types = observationTypes;
    }

    if (statistic) {
      params.statistic = statistic;
    }

    return this.endPoint.post('full_statistics', params);
  }

  getKPIStats(year: any, projectId?: string | string[]): Observable<any> {
    const params: any = {};
    const projectIds = Array.isArray(projectId) ? projectId : [projectId];

    // Preserve the existing single-project API contract while supporting multi-project queries.
    if (projectIds.length === 1 && projectIds[0]) {
      params.project_id = projectIds[0];
    } else if (projectIds[0]) {
      params.project_ids = projectIds;
    }

    if (year !== null && year !== undefined) {
      params.year = year;
    }
    return this.endPoint.get('analytics/kpi', params);
  }

  getStatsByProjectYear(id: any, year: any): Observable<any> {
    return this.endPoint.post('project_statistics', {
      project_id: id,
      year: year,
    });
  }

  projectsReportStatistics(
    filter?: ProjectsArchiveFilter,
  ): Observable<ProjectsReportStatisticsModel[]> {
    // Map the archive selection to exactly one valid backend payload. "all" uses
    // include_archived (both active and archived); archived/not_archived use
    // is_archived. The two flags must never be combined — include_archived makes
    // the backend ignore is_archived.
    const body =
      filter === 'archived'
        ? { is_archived: true }
        : filter === 'not_archived'
        ? { is_archived: false }
        : filter === 'all'
        ? { include_archived: true }
        : {};

    return this.endPoint.post('projects_report_statistics', body);
  }

  //#endregion // End

  /* ******************
   *  *** Fetch Methods End  ***
   *  ****************** */

  //#reigon -- refactored methods

  //#reigon //end

  api_getChartDataRefactored(
    projectId: string,
    params?: any,
  ): Observable<ObservationsChartDataModel> {
    return this.endPoint.get('charts', params || null, `projects/${projectId}/observations`);
  }

  api_getCumulativeChartData(
    projectId: string,
    params?: any,
  ): Observable<ObservationsChartDataModel> {
    return this.endPoint.get(
      'cumulative-charts',
      params || null,
      `projects/${projectId}/observations`,
    );
  }

  /* ******************
   *  *** Methods Start  ***
   *  ****************** */
  async fetchChartData(
    projectId: string,
    dateParams?: any,
    observationTypes?: string[],
  ): Promise<ChartData> {
    if (!projectId) {
      return;
    }
    let params = null;
    if (dateParams?.to || dateParams?.from) {
      params = dateParams;
    }

    // Build endpoint with observation type query params if provided
    let endpoint = 'charts';
    if (observationTypes && observationTypes.length > 0) {
      const queryParams = observationTypes
        .map((type) => `observation_type[]=${encodeURIComponent(type)}`)
        .join('&');
      endpoint += `?${queryParams}`;
      // If date params exist, append them to the query string
      if (params) {
        endpoint += `&from=${params.from}&to=${params.to}`;
        params = null; // Clear params since we've added them to the URL
      }
    }

    return new Promise((resolve, reject) => {
      this.endPoint.get(endpoint, params, `projects/${projectId}/observations`).subscribe(
        (chartDataResponse) => {
          this._observationChartData = {};
          this._observationChartData.categoriesChart = chartDataResponse.data.bar_chart;
          this._observationChartData.userChart = chartDataResponse.data.user_chart;
          this._observationChartData.pieChart = chartDataResponse.data.pie_chart;
          resolve(chartDataResponse.data);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  async fetchCumulativeChartData(projectId: string, dateParams?: any): Promise<ChartData> {
    if (!projectId) {
      return;
    }
    let params = null;
    if (dateParams?.to || dateParams?.from) {
      params = dateParams;
    }
    return new Promise((resolve, reject) => {
      this.api_getCumulativeChartData(projectId, params).subscribe(
        (chartDataResponse) => {
          this._cumulativeObservationChartData = {};
          this._cumulativeObservationChartData.categoriesChart = chartDataResponse.data.bar_chart;
          this._cumulativeObservationChartData.userChart = chartDataResponse.data.user_chart;
          this._cumulativeObservationChartData.pieChart = chartDataResponse.data.pie_chart[0];
          resolve(chartDataResponse.data);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  getAllProjectsObservationsAnalytics(observationTypes?: string[]) {
    return new Promise((resolve, reject) => {
      // Build endpoint with query params
      // The API expects multiple observation_type parameters: observation_type[]=RFI&observation_type[]=NCR
      let endpoint = 'observations/analytics';

      if (observationTypes && observationTypes.length > 0) {
        // Manually build query string with multiple observation_type parameters
        const queryParams = observationTypes
          .map((type) => `observation_type[]=${encodeURIComponent(type)}`)
          .join('&');
        endpoint += `?${queryParams}`;
      }

      this.endPoint.get(endpoint).subscribe(
        (chartDataResponse) => {
          this._observationChartData = {};
          this._observationChartData.categoriesChart = chartDataResponse.data.bar_chart;
          this._observationChartData.userChart = chartDataResponse.data.user_chart;
          this._observationChartData.pieChart = chartDataResponse.data.pie_chart;
          this._observationChartData.assigneeChart = chartDataResponse.data.assignee_chart;
          resolve(chartDataResponse.data);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }
}
